/* @flow */
import { graphql } from 'react-apollo';
import { partition, xor, isEmpty } from 'lodash';
import * as React from 'react';
import ContentLoader from 'react-content-loader';
import cx from 'classnames';
import gql from 'graphql-tag';
import idx from 'idx';

import { isAdmin } from 'shapes/Affiliation';
import { roleNameSingular } from 'utils/roleHelpers';
import AdminResignModal from 'admin/components/AdminResignModal.jsx';
import Button from 'components/Button.jsx';
import Buttons from 'components/Buttons.jsx';
import Coin from 'components/Coin.jsx';
import CircleSpinner from 'components/CircleSpinner.jsx';
import CurrentUser from 'auth/CurrentUser';
import Glyph from 'components/Glyph.jsx';
import GroupAvatar from 'groups/components/GroupAvatar.jsx';
import Image from 'components/Image.jsx';
import LayerTrigger from 'components/LayerTrigger.jsx';
import MediaObject from 'components/MediaObject.jsx';
import ModalTrigger from 'components/ModalTrigger.jsx';
import OrgRoleDropdownMenu from 'groups/components/OrgRoleDropdownMenu.jsx';
import SettingsRemoveSchoolModal from './SettingsRemoveSchoolModal.jsx';
import Text from 'components/Text.jsx';

import './styles/SettingsSchoolsListItem.scss';

import { AvatarImages } from 'constants/AvatarConstants.js';

const AVATAR_NUMBER = 3;

type Props = {
  affiliation: ?Object,
  school: *,
  groups: *,
  submitting?: ?boolean,
  staffClass: *,
  data: ReactApolloData<SettingsSchoolsListItemQuery>,
};

type State = {
  open: boolean,
  hovered: boolean,
  savingRole: boolean,
  roles: OrganizationRole[],
};

// prettier-ignore
const Ghost = () => (
  <ContentLoader
		height={42}
		width={500}
		speed={1}
		primaryColor={"#f2f3f5"}
		secondaryColor={"#ffffff"}
	>
		<rect x="48" y="15" rx="4" ry="4" width="300" height="10" />
		<circle cx="21.730023818941984" cy="21.670023818941985" r="14" />
	</ContentLoader>
)

class SettingsSchoolsListItem extends React.Component<Props, State> {
  static defaultProps = {
    submitting: false,
    groups: [],
  };

  state: State = {
    open: false,
    hovered: false,
    savingRole: false,
    roles: this.props.affiliation ? this.props.affiliation.roles : [],
  };

  componentDidUpdate(prevProps: Props) {
    if (!prevProps.affiliation || !this.props.affiliation) {
      return;
    }

    if (prevProps.affiliation.roles.length !== this.props.affiliation.roles.length) {
      this.updateRoles(this.props.affiliation.roles);
    }
  }

  updateRoles = (roles: OrganizationRole[]) => {
    this.setState({ roles });
  };

  renderHoverContent() {
    const { groups, affiliation, school, staffClass, data } = this.props;
    const isParentOrgAdmin = idx(data.organizations[0], _ => _.isParentOrgAdmin);

    // If there's no staff class, then you're not an admin and can leave
    const canLeave = staffClass ? staffClass.permissions.can_leave : true;

    const groupsByOwned = partition(groups, group =>
      group.owners.find(user => user.id === CurrentUser.getUserId())
    );

    const removeSchoolModal = (
      <SettingsRemoveSchoolModal
        affiliation={affiliation}
        ownedGroups={groupsByOwned[0]}
        subscribedGroups={groupsByOwned[1]}
        school={school}
      />
    );

    const canRemoveAdminship = affiliation && isAdmin(affiliation) && !isParentOrgAdmin;

    if (!canRemoveAdminship && !canLeave) {
      return this.renderGroupAvatars();
    }

    return (
      <Buttons align="right">
        {canRemoveAdminship && (
          <ModalTrigger
            className="SettingsSchoolsListItem-removeAdminButton"
            modal={<AdminResignModal affiliation={affiliation} school={school} />}>
            <Button color="lava" disabled={this.props.submitting} level="secondary" size="xsmall">
              Remove me as admin
            </Button>
          </ModalTrigger>
        )}
        {canLeave && (
          <ModalTrigger className="SettingsSchoolsListItem-leaveButton" modal={removeSchoolModal}>
            <Button color="lava" disabled={this.props.submitting} level="secondary" size="xsmall">
              Leave
            </Button>
          </ModalTrigger>
        )}
      </Buttons>
    );
  }

  renderGroupAvatars() {
    const { groups } = this.props;
    const avatars = groups
      .slice(0, AVATAR_NUMBER)
      .map(g => (
        <GroupAvatar className="SettingsSchoolsListItem-group" key={g.id} group={g} size="small" />
      ));
    return (
      <div>
        {avatars}
        {groups.length > AVATAR_NUMBER && (
          <Coin
            className="SettingsSchoolsListItem-group"
            color="white"
            border="brand"
            size="xlarge">
            +{groups.length - AVATAR_NUMBER}
          </Coin>
        )}
      </div>
    );
  }

  renderIcon(school: *) {
    const { affiliation } = this.props;
    const userIsAdmin = affiliation && isAdmin(affiliation);
    const image = <Image height={32} width={32} src={AvatarImages.ORG_AVATAR} />;
    const badge = (
      <Coin
        className="SettingsSchoolsListItem-icon-badge"
        color={userIsAdmin ? 'midnight' : 'shamrock'}
        size="small">
        <Glyph name={userIsAdmin ? 'crown' : 'check'} color="white" solo />
      </Coin>
    );

    if (!isEmpty(school.admins)) {
      return image;
    }

    return (
      <div>
        {image}
        {badge}
      </div>
    );
  }

  renderByline() {
    const { affiliation } = this.props;
    if (!affiliation) {
      return null;
    }

    const roleString = this.state.roles.map(e => roleNameSingular[e]).join(', ');

    if (
      !affiliation.can_have_role_changed ||
      this.state.roles.length === 0 ||
      this.state.savingRole
    ) {
      return (
        <div>
          <span className="SettingsSchoolsListItem-roleString">{roleString}</span>
          {this.state.savingRole && (
            <CircleSpinner
              className="SettingsSchoolsListItem-roleSpinner"
              color="shamrock"
              size="small"
            />
          )}
        </div>
      );
    }

    const onClose = roles => {
      if (xor(roles, this.state.roles).length > 0) {
        this.setState({ savingRole: true, roles });
      }
      this.setState({ open: false });
    };

    const roleDropdown = (
      <OrgRoleDropdownMenu
        isCurrentUser
        organization={this.props.school}
        roles={this.state.roles}
        potentialRoles={affiliation.potential_roles}
        userId={CurrentUser.getUserId()}
        onClose={onClose}
        onFinishSave={() => this.setState({ savingRole: false })}
      />
    );

    return (
      <LayerTrigger layer={roleDropdown} trigger="click" placement="bottom" alignment="left">
        <span
          className="SettingsSchoolsListItem-editableRole"
          onClick={() => this.setState({ open: !this.state.open })}>
          <span className="SettingsSchoolsListItem-editableRole-text">{roleString}</span>
          <Glyph
            name="chevron-down"
            color="thunder"
            size="xxsmall"
            className={cx({
              'SettingsSchoolsListItem-editableRole-chevron': true,
              'SettingsSchoolsListItem-editableRole-chevronClosed': !this.state.open,
              'SettingsSchoolsListItem-editableRole-chevronOpen': this.state.open,
            })}
          />
        </span>
      </LayerTrigger>
    );
  }

  render() {
    const { school, submitting, data } = this.props;

    if (data.loading) {
      return <Ghost />;
    }

    return (
      <div
        className="SettingsSchoolsListItem"
        onMouseEnter={() => this.setState({ hovered: true })}
        onMouseLeave={() => this.setState({ hovered: false })}>
        <MediaObject center figure="right">
          <MediaObject center gutter="xsmall">
            <div className="SettingsSchoolsListItem-icon">{this.renderIcon(school)}</div>
            <Text color="thunder" size="small" className="SettingsSchoolsListItem-content">
              <Text
                color={submitting ? 'inherit' : 'pitch'}
                truncate
                className="SettingsSchoolsListItem-name">
                {school.name}
              </Text>
              {this.renderByline()}
            </Text>
          </MediaObject>
          <div className="SettingsSchoolsListItem-avatarsActions">
            {this.state.hovered ? this.renderHoverContent() : this.renderGroupAvatars()}
          </div>
        </MediaObject>
      </div>
    );
  }
}

const withData = graphql(
  gql`
    query SettingsSchoolsListItem($organizationUuid: String!) {
      organizations(uuids: [$organizationUuid]) {
        uuid
        id
        isParentOrgAdmin
      }
    }
  `,
  {
    options: ({ school }) => ({
      variables: {
        organizationUuid: school.uuid,
      },
    }),
  }
);

export { SettingsSchoolsListItem };

export default withData(SettingsSchoolsListItem);
